<template>
  <div class="termination-info">
    <div v-if="associate.termination" class="termination-info__saved">
      <strong>{{ savedSeparationType.label }}</strong>
      <p class="termination-info__description">{{ savedSeparationType.description }}</p>
      <p class="termination-info__summary" v-html="separationSummary" />
    </div>

    <a-modal
      :visible="visible"
      :title="modalTitle"
      :footer="null"
      :destroy-on-close="false"
      width="640px"
      @cancel="cancelTermination"
    >
      <div class="termination-form">
        <div class="termination-form__field">
          <label for="profile-separation-type">Separation Type <span class="required-field">*</span></label>
          <a-select
            id="profile-separation-type"
            v-model="newTermination.type"
            aria-label="Separation Type"
            class="w-full"
            option-label-prop="label"
            placeholder="Select separation type"
          >
            <a-select-option
              v-for="option in separationTypeOptions"
              :key="option.value"
              :value="option.value"
              :label="option.label"
            >
              <div class="termination-form__option">
                <strong>{{ option.label }}</strong>
                <span>{{ option.description }}</span>
              </div>
            </a-select-option>
          </a-select>
          <span v-if="selectedSeparationType" class="termination-form__hint">
            {{ selectedSeparationType.description }}
          </span>
        </div>

        <div class="termination-form__field">
          <label for="profile-separation-date">Last Work Day <span class="required-field">*</span></label>
          <a-date-picker
            id="profile-separation-date"
            v-model="newTermination.termination_date"
            placeholder="Enter separation date"
            class="w-full"
            :format="visualDateFormat"
            :disabled-date="disabledTerminationDate"
          />
          <span class="termination-form__hint">
            When you pick a date, the associate will be separated. Their profile will remain active until automatic
            disable 30 days after the separation date.
          </span>
        </div>

        <div class="termination-form__field">
          <label for="profile-separation-reason">Reason <span class="required-field">*</span></label>
          <a-textarea
            id="profile-separation-reason"
            v-model="newTermination.reason"
            :auto-size="true"
            placeholder="Separation Reason"
          />
        </div>

        <div class="termination-form__actions">
          <a-button :disabled="savingTermination" @click="cancelTermination">Cancel</a-button>
          <a-button
            type="danger"
            :loading="savingTermination"
            :disabled="saveTerminationDisabled"
            @click="openTerminationConfirm"
          >
            {{ submitButtonLabel }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import util from '@/util'
import {
  getSeparationDateLabel,
  getSeparationStatusLabel,
  getSeparationSubmissionDateLabel,
  getSeparationType,
  isResignationSeparationType,
  SEPARATION_TYPES
} from '@/modules/profile/utils/separation'
import { Button, DatePicker, Input, Modal, notification, Select } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-button': Button,
    'a-date-picker': DatePicker,
    'a-modal': Modal,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-textarea': Input.TextArea
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      newTermination: {
        type: undefined,
        reason: undefined,
        termination_date: undefined
      },
      savingTermination: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    paymentInfo() {
      return this.associate?.payment_info ?? {}
    },

    paymentType() {
      return this.paymentInfo.type
    },

    modalTitle() {
      return this.isEditingTermination ? 'Edit Separation' : 'Separate associate'
    },

    submitButtonLabel() {
      return this.isEditingTermination ? 'Update Separation' : 'Separate'
    },

    isEditingTermination() {
      return Boolean(this.associate?.termination)
    },

    associateName() {
      return [this.associate?.first_name, this.associate?.last_name].filter(Boolean).join(' ')
    },

    separationTypeOptions() {
      if (this.paymentType === 'Contractor') {
        return [SEPARATION_TYPES.dnr, SEPARATION_TYPES.break_up]
      }

      return [SEPARATION_TYPES.terminate, SEPARATION_TYPES.resign]
    },

    selectedSeparationType() {
      return getSeparationType(this.newTermination.type)
    },

    savedSeparationType() {
      const type = this.associate?.termination?.type

      return {
        label: getSeparationStatusLabel(type),
        description: getSeparationType(type)?.description || ''
      }
    },

    terminationAuthorName() {
      const terminationAuthor = this.associate?.termination?.author

      return terminationAuthor
        ? util.combineAssociateName(terminationAuthor.first_name, terminationAuthor.last_name)
        : '—'
    },

    separationSummary() {
      const termination = this.associate?.termination

      if (!termination) {
        return ''
      }

      const author = this.terminationAuthorName
      const date = this.formatDateString(termination.termination_date)
      const reason = termination.reason || '—'
      const submissionDate = this.formatDateString(termination.created_at)
      const separationDateLabel = getSeparationDateLabel(termination.type)
      const submissionDateLabel = getSeparationSubmissionDateLabel(termination.type)

      return `${separationDateLabel}: <strong>${date}</strong>. ${submissionDateLabel}: ${submissionDate}. Recorded by <strong>${author}</strong>. Reason: ${reason}.`
    },

    saveTerminationDisabled() {
      return (
        this.savingTermination ||
        !this.newTermination.type ||
        !this.newTermination.termination_date ||
        !this.newTermination.reason ||
        this.newTermination.reason.length === 0
      )
    },

    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.prepareTermination()
      } else {
        this.resetNewTermination()
      }
    }
  },
  methods: {
    ...profileActions([
      'patchAssociateTermination',
      'postAssociateTerminate',
      'postAssociateResign',
      'reloadAssociate'
    ]),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    disabledTerminationDate(current) {
      if (this.paymentType === 'Contractor') {
        return current && moment(current).startOf('day').isAfter(moment().startOf('day'))
      }

      return false
    },

    formatDateString(dateString) {
      return dateString ? moment(dateString).utc().format('MM/DD/YYYY') : '—'
    },

    cancelTermination() {
      this.resetNewTermination()
      this.$emit('update:visible', false)
    },

    resetNewTermination() {
      this.newTermination = {
        type: undefined,
        termination_date: undefined,
        reason: undefined
      }
    },

    prepareTermination() {
      const termination = this.associate?.termination

      if (!termination) {
        this.resetNewTermination()
        return
      }

      this.newTermination = {
        type: termination.type,
        termination_date: termination.termination_date ? moment.utc(termination.termination_date) : undefined,
        reason: termination.reason ?? ''
      }
    },

    openTerminationConfirm() {
      const selectedType = this.separationTypeOptions.find(option => option.value === this.newTermination.type)

      if (this.isEditingTermination) {
        Modal.confirm({
          title: `Update separation for ${this.associateName}?`,
          content: 'The current separation document will be deleted and replaced using the updated information.',
          okText: 'Update Separation',
          cancelText: 'Keep Current Separation',
          onOk: () => this.updateTermination(),
          onCancel() {}
        })
        return
      }

      Modal.confirm({
        title: `Record separation as ${selectedType.label}?`,
        content: selectedType.description,
        onOk: async () => {
          await this.setTermination()
        },
        onCancel() {}
      })
    },

    async setTermination() {
      const separationType = this.newTermination.type
      const termination = {
        reason: this.newTermination.reason,
        termination_date: this.newTermination.termination_date
      }
      const payload = {
        user_id: this.associate.user_id,
        associate_number: this.associate.associate_number,
        termination: {
          ...termination,
          termination_date: moment(termination.termination_date).utc().startOf('day').toISOString()
        },
        associate_id: this.associate.id
      }

      const isFuture = moment(payload.termination.termination_date).isAfter(moment().utc().startOf('day'))

      try {
        if (isResignationSeparationType(separationType)) {
          await this.postAssociateResign(payload)
        } else {
          await this.postAssociateTerminate(payload)
        }

        this.showNotification(
          'success',
          'Separation Set',
          this.getTerminationSuccessMessage(payload, isFuture, separationType)
        )

        this.cancelTermination()
        this.reloadAssociate(true)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      }
    },

    async updateTermination() {
      this.savingTermination = true

      try {
        await this.patchAssociateTermination({
          associate_id: this.associate.id,
          termination: {
            type: this.newTermination.type,
            termination_date: moment(this.newTermination.termination_date).utc().startOf('day').toISOString(),
            reason: this.newTermination.reason
          }
        })

        this.showNotification('success', 'Separation Updated', 'Separation details and document were updated.')
        this.cancelTermination()
        this.reloadAssociate(true)
      } catch (error) {
        if (error?.response?.status === 404) {
          this.cancelTermination()
          this.reloadAssociate(true)
        }

        this.showNotification('error', 'Error', this.getErrorMessage(error))
      } finally {
        this.savingTermination = false
      }
    },

    getErrorMessage(error) {
      const data = error?.response?.data

      if (data && typeof data === 'object') {
        const messages = Object.values(data)
          .flatMap(value => (Array.isArray(value) ? value : [value]))
          .filter(Boolean)

        if (messages.length) {
          return messages.join('\n')
        }
      }

      return typeof error === 'string' ? error : error?.message
    },

    getTerminationSuccessMessage(payload, isFuture, separationType) {
      const status = getSeparationStatusLabel(separationType)

      if (isFuture) {
        const date = this.formatDateString(moment(payload.termination.termination_date).add(1, 'day'))

        return `Separation is scheduled for ${date}. Type: ${this.selectedSeparationType.label}`
      }

      return `Separation recorded. Status: ${status}`
    }
  }
}
</script>

<style scoped>
.termination-info__saved {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.termination-info__description,
.termination-info__summary {
  margin: 0;
}

.termination-info__description {
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.termination-info__summary {
  color: var(--text-color);
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.termination-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.termination-form__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.termination-form__field label {
  font-weight: 600;
}

.termination-form__hint {
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.termination-form__option {
  display: flex;
  flex-direction: column;
  white-space: normal;
}

.termination-form__option span {
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.termination-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.ant-calendar-picker {
  margin: 0;
  min-width: unset !important;
}
</style>
